/**
 * Generates HTML email template for payment confirmation ("Trip Paid by Client")
 * Uses shared layout from emailLayout.js for header/footer.
 */

import { formatUTCToDateTime } from '@/utils/formatters';
import { getAirportTimezone } from '@/utils/airportTimezone';
import { icon, spacer, emailDocOpen, emailDocClose, emailHeader, emailFooter } from '../emailLayout';
import { getAircraftPhotos } from '@/features/tails/utils';
import { formatPrice, trunc2 } from '@/utils';

const formatFullDate = (dateString) => {
  if (!dateString) return '';
  const d = new Date(dateString);
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Determine trip type label from leg itinerary
 */
const getTripTypeLabel = (legs) => {
  if (!legs || legs.length === 0) return '';
  if (legs.length === 1) return 'One Way';
  if (legs.length === 2) {
    const first = legs[0];
    const second = legs[1];
    if (
      first.departure_airport === second.arrival_airport &&
      first.arrival_airport === second.departure_airport
    ) {
      return 'Round Trip';
    }
  }
  return 'Multi-Leg';
};

/**
 * Get route display string from legs
 */
const getRouteDisplay = (legs) => {
  if (!legs || legs.length === 0) return '';
  if (legs.length === 1) return `${legs[0].departure_airport || ''} → ${legs[0].arrival_airport || ''}`;
  return legs.map(l => l.departure_airport).concat(legs[legs.length - 1].arrival_airport).filter(Boolean).join(' → ');
};

/**
 * Build the aircraft model display name
 */
const getModelName = (aircraft) => {
  if (!aircraft) return '';
  if (aircraft.aircraft_make_model) {
    const { manufacturer } = aircraft.aircraft_make_model;
    const makeModel = aircraft.make_model || aircraft.aircraft_make_model.make_model || '';
    if (manufacturer && makeModel) {
      const modelPart = makeModel.replace(new RegExp(`^${manufacturer}[- ]*`, 'i'), '');
      return `${manufacturer} ${modelPart}`.trim();
    }
    return makeModel || '';
  }
  return aircraft.make_model || aircraft.tail_number || '';
};

/**
 * Build the payment confirmation email HTML
 *
 * @param {Object} params
 * @param {Object} params.quote - Full quote object
 * @param {Object} params.selectedBidOption - The accepted bid option
 * @param {Object} params.paymentResult - Result from capturePassportPayment
 *   { transactionId, reference, totalAmount, amount, fees, paymentMethod }
 * @param {string} params.bookingNumber - Generated booking number (e.g. "12AB-34CD-56EF")
 * @param {string} [params.logoUrl] - Optional logo URL override
 * @returns {string} HTML email body
 */
export const buildPaymentConfirmationEmailHtml = ({
  quote,
  selectedBidOption,
  paymentResult,
  bookingNumber,
  logoUrl,
  organization,
  isBroker = false,
}) => {
  const client = quote?.client;
  const clientName = [client?.first_name, client?.last_name].filter(Boolean).join(' ') || 'Valued Client';
  const legs = quote?.bid_items?.[0]?.bid?.leg_itinerary || [];
  const aircraft = selectedBidOption?.aircraft;
  const operator = selectedBidOption?.operator;

  const tripType = getTripTypeLabel(legs);
  const routeDisplay = getRouteDisplay(legs);
  const departureDates = legs.map((leg, i) => {
    const tz = getAirportTimezone(leg.departure_airport);
    const date = leg.departure_date ? formatUTCToDateTime(leg.departure_date, tz, 'dddd, MMMM D, YYYY') : '';
    const time = leg.departure_date ? formatUTCToDateTime(leg.departure_date, tz, 'HH:mm') : '';
    return {
      route: `${leg.departure_airport || ''} → ${leg.arrival_airport || ''}`,
      date,
      time,
      label: legs.length > 1 ? `Leg ${i + 1}` : 'Departure Date',
    };
  }).filter(d => d.date);
  const passengerCount = quote?.passenger_count || quote?.bid_items?.[0]?.bid?.passenger_count || '';

  // Aircraft details
  const modelName = getModelName(aircraft);
  const imageUrl = getAircraftPhotos(aircraft);
  const operatorName = operator?.name || '';
  const operatorLocation = [operator?.city, operator?.state].filter(Boolean).join(', ');
  const operatorDisplay = operatorLocation ? `${operatorName} • ${operatorLocation}` : operatorName;
  const price = formatPrice(trunc2(selectedBidOption?.total_price || selectedBidOption?.final_price));

  // Badges
  const badges = [];
  if (operator?.argus_rating && operator.argus_rating !== 'No') {
    badges.push({ label: operator.argus_rating.toUpperCase(), bg: '#F3F4F6', color: '#374151', iconName: 'trophy' });
  }
  if (operator?.wyvern_rating && operator.wyvern_rating !== 'No') {
    badges.push({ label: 'WINGMAN', bg: '#F3F4F6', color: '#374151', iconName: 'medal' });
  }
  if (aircraft?.aircraft_features?.no_aog) {
    badges.push({ label: 'NO AOG', bg: '#DCFCE7', color: '#15803D', iconName: 'check' });
  }

  const badgesHtml = badges.map(b =>
    `<span style="display:inline-block;padding:4px 10px;background:${b.bg};color:${b.color};font-size:11px;font-weight:600;border-radius:16px;margin-right:6px;margin-bottom:4px;">${icon(b.iconName, 14, b.color)} ${b.label}</span>`
  ).join('');

  // Amenities
  const amenities = aircraft?.aircraft_features?.custom_features || [];
  const amenitiesHtml = amenities.map(a =>
    `<span style="display:inline-block;padding:4px 12px;background:#F3F4F6;color:#374151;font-size:12px;font-weight:500;border-radius:16px;margin-right:6px;margin-bottom:6px;">${icon('check', 14, '#374151')} ${a}</span>`
  ).join('');

  // Quote validity
  const validUntil = quote?.valid_until ? formatFullDate(quote.valid_until) : '';

  // Price breakdown from the bid option fields
  const flightCost = (parseFloat(selectedBidOption?.operator_cost) || 0) +
    (parseFloat(selectedBidOption?.broker_markup) || 0) +
    (parseFloat(selectedBidOption?.platform_fee) || 0);
  const federalExciseTax = parseFloat(selectedBidOption?.federal_excise_tax) || 0;
  const segmentTax = parseFloat(selectedBidOption?.segment_tax) || 0;
  const totalDue = selectedBidOption?.final_price;

  // Credit card fee from payment result
  const ccFee = (paymentResult?.fees || []).find(f =>
    f?.name?.toLowerCase()?.includes('credit') ||
    f?.name?.toLowerCase()?.includes('card') ||
    f?.type?.toLowerCase()?.includes('card')
  );

  const creditCardFeeAmount = ccFee ? parseFloat(ccFee.amount || ccFee.value || 0) : (totalDue * 0.03);

  // Payment method display
  const pm = paymentResult?.paymentMethod || {};
  const cardBrand = (pm.brand || pm.cardType || pm.type || 'VISA').toUpperCase();

  return `
${emailDocOpen({ title: 'Trip Paid by Client' })}
${emailHeader({ title: 'Trip Paid by Client', organization, isBroker })} 

        ${spacer()}

        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <!-- ===== CLIENT / BOOKING NUMBER row ===== -->
          <tr>
            <td style="padding:16px 32px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td>
                    <span style="font-size:12px;text-transform:uppercase;color:#6B7280;letter-spacing:0.5px;font-weight:600;">Client</span><br/>
                    <span style="font-size:15px;font-weight:600;color:#111827;">${clientName}</span>
                  </td>
                  <td align="right">
                    <span style="font-size:12px;text-transform:uppercase;color:#6B7280;letter-spacing:0.5px;font-weight:600;">Booking Number</span><br/>
                    <span style="font-size:15px;font-weight:600;color:#111827;">${bookingNumber || ''}</span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          </table>

        ${spacer()}

        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">

          <!-- ===== CONFIRMATION MESSAGE (white bg, no card) ===== -->
          <tr>
            <td style="padding:28px 32px 24px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td width="56" valign="top" style="padding-right:16px;">
                    <div style="width:48px;height:48px;background-color:#DCFCE7;border-radius:50%;text-align:center;line-height:48px;">
                      ${icon('check', 24, '#16A34A')}
                    </div>
                  </td>
                  </tr>
                  <tr>
                  <td valign="middle" style="padding-top:12px;">
                    <p style="margin:0;font-size:14px;color:#374151;line-height:1.6;">
                      Your quote for ${clientName} has been paid for and booking number has been assigned. Kindly review the selected aircraft below.
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          </table>

        ${spacer()}

        <!-- ===== TRIP DETAILS ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td style="padding:20px 32px 8px;">
              <span style="font-size:12px;text-transform:uppercase;color:#6B7280;letter-spacing:0.5px;font-weight:600;">Trip Details</span>
            </td>
          </tr>
          <!-- Row 1: Passenger Count + Round Trip -->
          <tr>
            ${passengerCount ? `
            <td style="padding:12px 32px;" valign="top" width="50%">
              <table role="presentation" cellpadding="0" cellspacing="0">
                <tr>
                  <td width="44" valign="top" style="padding-right:12px;">
                    <div style="width:40px;height:40px;background-color:#F3F4F6;border-radius:50%;text-align:center;line-height:40px;">
                      ${icon('team', 20, '#6B7280')}
                    </div>
                  </td>
                  <td valign="middle">
                    <p style="margin:0 0 2px;font-size:14px;color:#6B7280;">Passenger Count</p>
                    <p style="margin:0;font-size:18px;font-weight:600;color:#111827;">${passengerCount}</p>
                  </td>
                </tr>
              </table>
            </td>` : ''}
            <td style="padding:12px 32px;" valign="top" ${passengerCount ? 'width="50%"' : ''}>
              <table role="presentation" cellpadding="0" cellspacing="0">
                <tr>
                  <td width="44" valign="top" style="padding-right:12px;">
                    <div style="width:40px;height:40px;background-color:#F3F4F6;border-radius:50%;text-align:center;line-height:40px;">
                      ${icon('plane', 20, '#6B7280')}
                    </div>
                  </td>
                  <td valign="middle">
                    <p style="margin:0 0 2px;font-size:14px;color:#6B7280;">${tripType || 'Trip'}</p>
                    <p style="margin:0;font-size:18px;font-weight:600;color:#111827;">${routeDisplay}</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <!-- Row 2: Departure Date(s) -->
          ${departureDates.map((d, i) => `
          <tr>
            <td style="padding:12px 32px ${i === departureDates.length - 1 ? '20px' : '0'};" valign="top" ${passengerCount ? 'colspan="2"' : ''}>
              <table role="presentation" cellpadding="0" cellspacing="0">
                <tr>
                  <td width="44" valign="top" style="padding-right:12px;">
                    <div style="width:40px;height:40px;background-color:#F3F4F6;border-radius:50%;text-align:center;line-height:40px;">
                      ${icon('calendar', 20, '#6B7280')}
                    </div>
                  </td>
                  <td valign="middle">
                    <p style="margin:0 0 2px;font-size:14px;color:#6B7280;">${d.label}${departureDates.length > 1 ? ` (${d.route})` : ''}</p>
                    <p style="margin:0;font-size:18px;font-weight:600;color:#111827;">${d.date}${d.time ? ` at ${d.time}` : ''}</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>`).join('')}
        </table>

        ${spacer()}

        <!-- ===== BOOKING NUMBER (gray bg section) ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td style="padding:20px 32px;background-color:#F9FAFB;">
              <p style="margin:0 0 4px;font-size:12px;text-transform:uppercase;color:#6B7280;letter-spacing:0.5px;font-weight:600;">Booking Number</p>
              <p style="margin:0;font-size:28px;font-weight:500;color:#111827;letter-spacing:1px;">${bookingNumber || ''}</p>
            </td>
          </tr>
        </table>

        ${spacer()}

        <!-- ===== AIRCRAFT IMAGE + DETAILS ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          ${imageUrl ? `
          <tr>
            <td style="padding:0;line-height:0;font-size:0;">
              <img src="${imageUrl}" alt="${modelName}" width="600" style="display:block;width:100%;height:auto;object-fit:cover;max-height:300px;" />
            </td>
          </tr>` : ''}
          <tr>
            <td style="padding:20px 32px;">
              <h2 style="margin:0 0 4px;font-size:20px;font-weight:500;color:#111827;">${aircraft?.tail_number || ''}</h2>
              <p style="margin:0 0 2px;font-size:13px;color:#6B7280;">${icon('plane', 14, '#6B7280')} ${modelName}</p>
              ${operatorDisplay ? `<p style="margin:0 0 12px;font-size:13px;color:#6B7280;">${icon('building', 14, '#6B7280')} ${operatorDisplay}</p>` : ''}

              <!-- Price Badge -->
              <div style="display:inline-block;padding:6px 14px;background-color:#E8F4FD;border-radius:16px;margin-bottom:12px;">
                <span style="font-size:14px;font-weight:700;color:#0C3E71;">${icon('priceTag', 14, '#0C3E71')} ${price}</span>
              </div>

              <!-- Rating Badges -->
              ${badgesHtml ? `<div style="margin-top:8px;">${badgesHtml}</div>` : ''}

              <!-- About -->
              ${aircraft?.description ? `
              <div style="margin-top:16px;">
                <h3 style="margin:0 0 6px;font-size:13px;font-weight:600;color:#6B7280;">About the Aircraft</h3>
                <p style="margin:0;font-size:13px;color:#6B7280;line-height:1.5;">${aircraft.description}</p>
              </div>` : ''}

              <!-- Amenities -->
              ${amenitiesHtml ? `
              <div style="margin-top:16px;">
                <h3 style="margin:0 0 8px;font-size:13px;font-weight:600;color:#6B7280;">Amenities</h3>
                <div>${amenitiesHtml}</div>
              </div>` : ''}
            </td>
          </tr>
        </table>

        <!-- ===== QUOTE VALIDITY NOTE ===== -->
        ${validUntil ? `
        ${spacer()}

        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#efefef;overflow:hidden;">
          <tr>
            <td style="padding:16px 32px;">
                <p style="margin:0;font-size:12px;line-height:1.5;color: #4c4c4c;">
                  This quote is valid until <strong>${validUntil}</strong>. Prices are subject to availability and may change.
                </p>
            </td>
          </tr>
        </table>` : ''}

        ${spacer()}

        <!-- ===== PRICE BREAKDOWN ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;overflow:hidden;">
          <!-- Title outside the card -->
          <tr>
            <td style="padding:20px 0px 12px;">
              <h2 style="margin:0;font-size:18px;font-weight:700;color:#4c4c4c;">Price Breakdown</h2>
            </td>
          </tr>
          <!-- Bordered card containing price table + paid by -->
          <tr>
            <td style="padding:0;background-color:#FFFFFF;border:1px solid #EBEBEB;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="overflow:hidden;">
                <!-- Aircraft model header -->
                <tr>
                  <td colspan="2" style="padding:14px 20px 8px;">
                    <span style="font-size:12px;font-weight:600;text-transform:uppercase;color:#6B7280;letter-spacing:0.5px;">${modelName}</span>
                  </td>
                </tr>
                <!-- Flight Cost -->
                <tr>
                  <td style="padding:8px 20px;font-size:14px;color:#374151;">Flight Cost</td>
                  <td align="right" style="padding:8px 20px;font-size:14px;font-weight: 500;color:#374151;">${formatPrice(flightCost)}</td>
                </tr>
                ${federalExciseTax > 0 ? `
                <tr>
                  <td style="padding:8px 20px;font-size:14px;color:#374151;">Federal Excise Tax (7.5%)</td>
                  <td align="right" style="padding:8px 20px;font-size:14px;color:#374151;font-weight: 500;">${formatPrice(federalExciseTax)}</td>
                </tr>` : ''}
                ${segmentTax > 0 ? `
                <tr>
                  <td style="padding:8px 20px;font-size:14px;color:#374151;">International or Segment Tax</td>
                  <td align="right" style="padding:8px 20px;font-size:14px;color:#374151;font-weight: 500;">${formatPrice(segmentTax)}</td>
                </tr>` : ''}
                <!-- Divider -->
                <tr>
                  <td colspan="2" style="padding:0 20px;"><div style="border-top:1px solid #E5E7EB;"></div></td>
                </tr>
                <!-- Total Due -->
                <tr>
                  <td style="padding:12px 20px;font-size:15px;font-weight:700;color:#111827;">Total Due</td>
                  <td align="right" style="padding:12px 20px;font-size:20px;font-weight:700;color:#111827;">${formatPrice(totalDue)}</td>
                </tr>
                ${creditCardFeeAmount > 0 ? `
                <tr>
                  <td style="padding:4px 20px 14px;font-size:14px;color:#374151;">Credit/Debit Card Fee (3%)</td>
                  <td align="right" style="padding:4px 20px 14px;font-size:14px;color:#374151;font-weight: 500;">${formatPrice(creditCardFeeAmount)}</td>
                </tr>` : ''}
                <!-- PAID BY divider -->
                <tr>
                  <td colspan="2" style="padding:0;"><div style="border-top:1px solid #E5E7EB;"></div></td>
                </tr>
                <!-- PAID BY -->
                <tr>
                  <td colspan="2" style="padding:16px 20px;">
                    <p style="margin:0 0 10px;font-size:10px;text-transform:uppercase;color:#9CA3AF;letter-spacing:0.5px;font-weight:600;">Paid By</p>
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                      <tr>
                        <td style="font-size:15px;font-weight:600;color:#111827;">${clientName}</td>
                        <td align="right" valign="middle">
                          <table role="presentation" cellpadding="0" cellspacing="0">
                            <tr>
                              <td style="vertical-align:middle;padding-right:8px;">
                                <span style="display:inline-block;background:#1A1F71;color:#FFFFFF;font-size:9px;font-weight:700;font-style:italic;padding:3px 8px;border-radius:3px;letter-spacing:0.5px;">${cardBrand}</span>
                              </td>
                              <td style="vertical-align:middle;">
                                <span style="font-size:14px;color:#374151;">&bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull;</span>
                              </td>
                            </tr>
                          </table>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>

        ${spacer()}

${emailFooter({ organization })}
${emailDocClose()}`;
};
